/* eslint-disable jsx-a11y/control-has-associated-label */
import * as React from "react";
import { Trans, useTranslation } from "react-i18next";
import { Avatar, StarRating } from "@audacia-hq/shared/components";
import Link from "next/link";
import { DateUtils, urlifyPseudo } from "@audacia-hq/shared/utils";

import { Rating } from "../../models/ratings";
import { Expert, ExpertIdentifier } from "../../models/expert";

interface Props {
  rating: Rating;
  expert?: Expert | ExpertIdentifier;
  expertLink?: boolean;
  showExpert?: boolean;
  showUser?: boolean;
  showAnswer?: boolean;
  className?: string;
}

const RatingCard: React.FC<Props> = ({
  rating, expert, expertLink = false, showExpert = false, showUser = false, showAnswer = false, className = "",
}) => {
  const { t } = useTranslation("expert");
  const expertPseudo = expert?.pseudo || t("common:anExpert");

  return (
    <div className={`flex flex-col py-5 gap-5 border-b border-slate-200 last:border-0 break-words ${className}`}>
      <div className="flex flex-col gap-2">
        <div className="flex gap-1">
          {rating.memberPseudo
            ? (
              <Trans
                i18nKey="reviewFrom"
                ns="common"
                components={[
                  <span className="font-semibold">{rating.memberPseudo}</span>,
                  <span className="font-semibold">{DateUtils.from(rating.createdAt).formatDate()}</span>,
                ]}
              />
            )
            : (<div />)}
        </div>

        {expert && showExpert && (
          <div className="flex gap-1 items-center">
            <Link className={expertLink ? "" : "pointer-events-none"} href={`/expert-detail/${urlifyPseudo(expert.pseudo)}`}>
              <Avatar
                name={expert.pseudo}
                uid={expert.expertUid}
                src={expert.picture}
                loading="lazy"
                className="w-12 h-12 rounded-full self-start border border-[#E2E8F0] p-0.5"
              />
            </Link>
            <Trans
              i18nKey="reviewFor"
              ns="common"
              components={[
                <span className="font-semibold">{expert.pseudo}</span>,
              ]}
            />
          </div>
        )}

        <StarRating
          value={rating.rate}
          max={5}
          disabled
          variant="yellow"
        />

        <span>{rating.comment}</span>
      </div>
      {(showAnswer && rating.ratingAnswer?.comment && rating.ratingAnswer.status === "PUBLISHED") && (
        <div className="ml-6 flex flex-col gap-2 p-3 rounded-lg bg-[#F8FAFC]">
          <div className="text-sm font-semibold mb-1">{t("common:reviewAnswer", { pseudo: expertPseudo })}</div>
          <div>{rating.ratingAnswer.comment}</div>
        </div>
      )}
    </div>
  );
};

export default RatingCard;
